import { DataType } from "./enums/DataType";
import { ConditionGroup } from "./EventRule";

export interface Field {
  fieldId?: string;
  eventId?: string;
  templateId?: string;
  name: string;
  translation?: string;
  dataType: DataType;
  additionalField: boolean;
  required?: boolean;
  manualCheck?: boolean;
  defaultValue?: unknown;
  options?: string[];
  /**
   * Optional per-option display colors for SINGLE_SELECT / MULTI_SELECT.
   * Keyed by option label, value is a 6-digit hex (`#RRGGBB`). Additive
   * side-map — `options` stays a plain string list. See optionChipColor.tsx.
   */
  optionColors?: Record<string, string>;
  /**
   * Dynamic-option marker for activity-rollup filter fields whose `options` are
   * per-event ObjectIds resolved at runtime (offerings / rooms / email
   * templates) rather than a static list. FieldsProvider fetches the source and
   * fills `options` (the hex ids) + `optionLabels` (id → human name). Absent on
   * every normal field.
   */
  optionSource?:
    | "offerings"
    | "treatments"
    | "lanes"
    | "rooms"
    | "emailTemplates"
    | "campaigns"
    | "imports";
  /**
   * Value → display-name map for {@link optionSource} fields. When present, the
   * SINGLE_SELECT / MULTI_SELECT cells show the name while storing the id.
   * Additive — normal fields leave it undefined and fall back to i18n.
   */
  optionLabels?: Record<string, string>;
  /**
   * i18n namespace to translate this field's {@link options} with, instead of
   * the shared `optionsMultiSingleSelect` catalogue.
   *
   * AUDIT 2026-08-14. `optionsMultiSingleSelect` is a FLAT key space shared by
   * every select field in the product, and it has already collided:
   * `CHECKIN_APP` lives there as the `GuestOrigin` label ("Beim Check-in
   * hinzugefügt") while the automation history means something else entirely by
   * the same key. Adding the automation-log sources there would either
   * mislabel the log source or break the guest-list `origin` column — the
   * collision is not fixable by adding keys.
   *
   * The automation history's own two fields (`source`, `trigger`) already have
   * correct catalogues under `automation` and `mailTemplateCreation`; the table
   * cells read them directly. Only the FILTER chip and value picker went
   * through the shared namespace, which is why they render raw enum keys today.
   * This pointer sends them to the same catalogue the cell uses, so there is
   * one truth per field rather than two.
   *
   * Absent on normal fields → the shared catalogue, unchanged.
   */
  optionNamespace?: string;
  /**
   * True while the {@link optionSource} fetch is still in flight. During that
   * window `options`/`optionLabels` are empty, so every stored id looks like a
   * dangling reference — the cells must render blank rather than claim the
   * referenced room/template/import was deleted. Undefined on normal fields.
   */
  optionsPending?: boolean;
  // DATE / DATE_TIME bounds are held as raw ISO-UTC strings (as emitted by the
  // date picker) — NOT re-wrapped into a Date. A `parseToLocalDate(iso).toString()`
  // round-trip turns the clean instant into a local-wall string that the picker's
  // `parseToUTCDate` then re-reads with a naive `+ "Z"`, double-applying the tz
  // offset (00:00 → 02:00 in CEST). Numeric (LONG/DOUBLE/…) bounds stay numbers.
  lowerBound?: number | Date | string;
  upperBound?: number | Date | string;
  /**
   * ISO 4217 code (e.g. "EUR") for {@link DataType.CURRENCY} fields. Fixed per
   * field at creation — every value in the column shares this currency so the
   * column stays a single-currency, Excel-summable money column. Undefined for
   * all other types. The stored VALUE is still a plain canonical number (dot
   * decimal); the code only drives input parsing, display formatting and the
   * Excel cell number format.
   */
  currencyCode?: string;
  /**
   * Conditional-display rule (additional fields only). When set, the field is
   * shown to / collected from a guest ONLY if the guest's other field values
   * satisfy this {@link ConditionGroup}. Evaluated live via
   * `evaluateConditionGroup`, with parity in main-backend /
   * website-builder-backend. Enforcement surfaces: the admin guest create/edit
   * form (PCreateGuest/PGuestDetails → AdditionFields) shows a SOFT override
   * (greyed/marked but still editable), and the public website registration
   * form enforces STRICTLY (unmet → field hidden, value stripped on submit).
   * The dense inline guest-list grid is intentionally NOT gated (admin-soft;
   * see AdditionFields). Absent on normal/built-in fields.
   */
  visibilityCondition?: ConditionGroup;
  /**
   * Conditional-editability rule (additional fields only). When set, the field
   * is settable ONLY if the guest satisfies this {@link ConditionGroup}. Unmet:
   * public path hard-locks the input; admin greys it but still allows override.
   * Independent of {@link visibilityCondition} (a field can be visible yet
   * locked). Absent on normal/built-in fields.
   */
  editableCondition?: ConditionGroup;
  /**
   * Per-option availability rules for SINGLE_SELECT / MULTI_SELECT (additional
   * fields only). Keyed by option label → {@link ConditionGroup}; the option is
   * selectable ONLY if the guest satisfies its condition. Options absent from
   * the map are always available. This is the generic dependent-dropdown lever
   * (e.g. a "Roomcategory" option keyed with `Hotel = VIP`). Additive — the
   * `options` list stays a plain string array. Absent on normal fields.
   */
  optionConditions?: Record<string, ConditionGroup>;
  /**
   * Optional per-option CAPACITY for SINGLE_SELECT / MULTI_SELECT — the max
   * number of guests that may hold each option (e.g. "5 red, 2 blue"). Keyed by
   * option label → cap; a missing key / absent map means "unlimited". Additive
   * side-map like {@link optionColors}. The live reservation counter
   * (optionReserved) is backend-only and never sent here; the guest pickers read
   * current usage separately via the option-usage endpoint.
   */
  optionCapacity?: Record<string, number>;
  /**
   * Entity scope of an ADDITIONAL field definition (Wave A). Absent/undefined
   * means GUEST (legacy definitions predate the field). SPEAKER/SPONSOR
   * definitions describe custom values on `event_speakers`/`event_sponsors`
   * and are fetched via `getAdditionalFields(..., target)`. Immutable after
   * create (backend `fieldTargetImmutable`). Never set on built-in fields.
   */
  appliesTo?: "GUEST" | "SPEAKER" | "SPONSOR";
  /**
   * SPEAKER/SPONSOR opt-in: may this definition's VALUES leave the admin
   * surfaces? Absent/false = internal-only, which is what every definition
   * created before this flag existed was promised — custom values reached
   * neither the website bake nor the attendee app. Ticking it publishes the
   * value on the public event website and in the app, where the per-entity
   * `hiddenFields` toggle can still hide it for one individual
   * speaker/sponsor. Meaningless on GUEST definitions.
   */
  publicOnWebsite?: boolean;
  /**
   * IT-04 — how protected this field's VALUES are. Absent means `NORMAL`.
   *
   * Two promises in the security papers hang on it: a restricted field's values
   * reach neither the check-in device nor the error reports. The organiser sets
   * it, because guessing from the name cannot work — the platform blocks the
   * Art. 9 key NAMES it can foresee (religion, health, diagnosis …) and that
   * list is itself the proof: "Allergien" is health data, is not on it, and
   * cannot be.
   *
   * `ART9` behaves exactly like `SENSITIVE` today. It records that the content
   * is a special category under GDPR Art. 9 so the retention and record-keeping
   * rules that follow have something to attach to. Never branch on one of the
   * two constants — anything that gates reads whether the value is not
   * `NORMAL`, or it misses whichever label the organiser happened to pick.
   */
  sensitivity?: "NORMAL" | "SENSITIVE" | "ART9";
  /**
   * GUEST only: this field's value is COMPUTED from the guest's own number
   * fields rather than entered by anybody.
   *
   * `{v:1, ast, text}` — the same arithmetic dialect a companion-limit rule
   * carries (`ConditionBuilder/expression-ast.ts`), with `text` the printed
   * echo the organiser reads. main-backend materialises the result onto the
   * guest document, which is why the value behaves like any other custom value
   * everywhere else: it filters, it sorts, it exports, it renders in a mail
   * marker.
   *
   * Its presence is what makes a field READ-ONLY. `editable` is minted in this
   * cockpit (`lib/utils/parser.ts`, `ViewFilterHandling`), never sent by the
   * backend, so the catalogue derives `editable: !formula` in one place and
   * every surface that already respects `editable` follows without its own
   * clause.
   *
   * ⚠ Ask {@link isComputedField}, never `!!field.formula`. See below.
   */
  formula?: { v: number; ast: unknown; text?: string } | Record<string, never>;
  editable: boolean;
}

/**
 * Is this field COMPUTED?
 *
 * <p>`!!field.formula` is the obvious spelling and it is wrong, because an
 * EMPTY wrapper is a real value on this property: `{}` is how a field stops
 * being computed. The backend has always asked
 * `formula != null && !isEmpty()`; this cockpit asked `!!field.formula` at
 * nine separate places, and `{}` is truthy in JavaScript — so a field an
 * organiser had just un-computed stayed read-only for good: locked in the
 * guest list, gone from the bulk-edit target picker, from the import mapper
 * and from the public form, with the edit dialog showing the "written
 * elsewhere" branch and no way back to a formula.
 *
 * <p>The backend now stores that absence as an ABSENCE (`$unset`), so the
 * empty wrapper no longer arrives over the wire. This predicate is what keeps
 * the in-session paths honest anyway: the edit dialog SENDS `{}`, and the
 * optimistic catalogue patch spreads whatever it sent straight onto the field.
 */
export function isComputedField(field: { formula?: unknown }): boolean {
  // Structurally typed on purpose: the public-form and AI-tool producers carry
  // their own narrower field shapes and agree only on this property. A
  // predicate every surface can call is the whole point.
  const formula = field.formula;
  if (formula === undefined || formula === null || typeof formula !== "object") return false;
  return Object.keys(formula as Record<string, unknown>).length > 0;
}
